/**
 * authFetch — backwards-compatible Response-like wrapper around the
 * shared axios instance.
 *
 * @deprecated Prefer `utils/apiClient.js` (axios) for new code.
 *
 * Why keep this shim? Many existing callers consume the standard `Response`
 * shape (`res.ok`, `res.status`, `await res.json()`). Migrating all of them
 * to axios in one go would be churny and risky. By re-implementing
 * `authFetch` ON TOP OF axios we get a true SINGLE HTTP-client policy
 * (axios's response interceptor for 401/403 handles auth failures here too)
 * while every legacy caller keeps working unchanged.
 *
 * The previous fetch-based implementation is preserved as a fallback for
 * the rare case that axios is somehow unavailable.
 */
import axios from 'axios';

const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/logout'];
const isAuthEndpoint = (url) => AUTH_ENDPOINTS.some((p) => url.includes(p));

const buildResponseLike = (status, data) => ({
  ok: status >= 200 && status < 300,
  status,
  // Mirror the Response interface the legacy callers expect.
  json: async () => (typeof data === 'string' ? safeParse(data) : data),
  text: async () => (typeof data === 'string' ? data : JSON.stringify(data ?? '')),
});

const safeParse = (text) => {
  try { return JSON.parse(text); } catch { return text; }
};

/**
 * @param {string} url - Fully-qualified URL (e.g. `${API}/api/...`).
 * @param {RequestInit & { body?: any }} options - Fetch-style options.
 * @returns {Promise<{ok:boolean,status:number,json:()=>Promise<any>,text:()=>Promise<string>}>}
 */
export const authFetch = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const headers = { ...(options.headers || {}) };
  let data = options.body;

  // axios auto-handles Content-Type for FormData/Blob/URLSearchParams.
  // For string bodies (JSON.stringify(...)), preserve user-provided headers.
  // If body is a plain string and no Content-Type set, default to JSON
  // (matches what fetch callers typically rely on via headers they passed).
  if (typeof data === 'string' && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await axios.request({
      url,
      method,
      headers,
      data,
      // Don't throw on non-2xx — we want fetch-style ok/status semantics.
      validateStatus: () => true,
    });

    // axios's installed response interceptor (in AuthContext.js) already
    // clears tokens on 401/403 from non-auth /api/ endpoints. Nothing to do
    // here — but we still notify legacy listeners for parity.
    if (
      url.includes('/api/') &&
      !isAuthEndpoint(url) &&
      (response.status === 401 || response.status === 403) &&
      localStorage.getItem('auth_token')
    ) {
      try { window.dispatchEvent(new CustomEvent('auth:cleared')); } catch {}
    }

    return buildResponseLike(response.status, response.data);
  } catch (err) {
    // Network error / aborted / etc. Return ok:false with status 0 so
    // callers handle it the same way they would a fetch network failure.
    return buildResponseLike(0, { detail: err?.message || 'Network error' });
  }
};

export default authFetch;
